/**
 * HP Collection Card — PoC controller
 *
 * Hydrates [data-hp-card] elements from TEST_DATA (below). The data shape
 * mirrors the final `product.metafields.logistics.variants_data` contract
 * that the HighPowered Backend (HPB) will produce — see
 * /Users/tobias/Documents/HighPowered/theme-dev/WAREHOUSE_LOGIC.md §3.2.
 *
 * Per-variant logistics data lives in a per-product `variants_data` map
 * keyed by variant ID, because Boost TURBO exposes variant metafields
 * only through `product.metafields`, not `variant.metafields` inside a
 * variant loop. Options are keyed by lowercase option name (variant.color,
 * variant.size, variant.flavor) — Boost does not expose option1/2/3.
 *
 * Pre-selection follows the three-tier rule in WAREHOUSE_LOGIC.md §3.3:
 *   1. Fastest delivery (stock_breakdown[0].priority ASC).
 *   2. Highest sales_score on tie.
 *   3. Upper-middle index (floor(N/2)) when sales_scores are tied / all 0.
 *   Final tie-break: variant_id ascending.
 *   Fallback: all OOS in group → first variant, Notify-Me UI takes over.
 */
(function () {
  'use strict';

  const FALLBACK_IMAGE =
    'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/no-image.png';

  // Bildbreite für Kachel + Karussell (Tobias 2026-08-12, docs/BILDGROESSEN_KACHELN.md).
  // Ohne ?width= liefert der Shopify-CDN die ORIGINALDATEI — 900 bis 2000 px für eine
  // Kachel von rund 300 CSS-Pixeln. Schlimmer: Bei Quelldateien mit Endung .webp
  // schickt der CDN an Safari (Mac UND iPhone) statt WebP ein PNG zurück, gemessen
  // 3'851 KB statt 331 KB für dasselbe Bild. Mit width= greift die Bildpipeline und
  // liefert 89 KB (Chrome) bzw. 430 KB (Safari).
  // 600 deckt beide Fälle: Desktop 299 CSS-px bei DPR 2 = 598, Mobile rund 180 bei DPR 3 = 540.
  const CARD_IMAGE_WIDTH = 600;

  // Hängt die Zielbreite an eine Shopify-CDN-URL. Fremde URLs und bereits
  // skalierte URLs bleiben unangetastet.
  function hpSized(url, width) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || CARD_IMAGE_WIDTH);
  }

  // crossorigin MUSS vor dem ersten src gesetzt werden: hp-thumb-fit.js liest die
  // Randpixel per Canvas, und das geht nur bei CORS-freigegebenen Bildern. Ohne das
  // Attribut müsste ein zweites Bild geladen werden — doppelter Traffic. Der Shopify-CDN
  // schickt access-control-allow-origin: *, das Attribut ist also gefahrlos.
  function hpPrepImg(img) {
    if (img && img.getAttribute('crossorigin') !== 'anonymous') {
      img.setAttribute('crossorigin', 'anonymous');
    }
    return img;
  }

  // ---------------------------------------------------------------------------
  // Speed-AP2 (2026-08-15): zentrale Bild-Pipeline der Karte. Grundsätze:
  //   1. EIN kanonischer src (hpSized, width=600) — byte-identisch über Skeleton,
  //      applyVariant und Karussell, damit derselbe Request/Cache-Eintrag
  //      wiederverwendet wird und das Bild beim Hydrieren nie neu lädt.
  //   2. srcset/sizes NUR auf Bildern, die damit GEBOREN wurden (Skeleton,
  //      Karussell-Slides). Einem nackten Bridge-Bild (alte customization.js im
  //      Push-Fenster) nachträglich ein srcset zu geben, liesse den Browser auf
  //      einen zweiten Kandidaten wechseln → Doppel-Download.
  //   3. Loading-Policy statt pauschalem removeAttribute('loading'). Der alte
  //      Pauschal-Entzug existierte, weil manche Browser nach einem src-Wechsel
  //      auf einem lazy Bild die Sichtbarkeit nicht neu bewerten (Grey-
  //      Placeholder-Regression 2026-05-30). Neu: im/nahe Viewport → loading
  //      entziehen (lädt sofort); ausserhalb → lazy BEHALTEN und per
  //      IntersectionObserver absichern (Self-Heal beim Reinscrollen).
  // Slot-Breiten am Dev-Theme gemessen (2026-08-15): 375px→169 CSS-px (2 Spalten),
  // 768px→219, 1280px→389, 1718px→514 (je 3 Spalten).
  const CARD_IMAGE_WIDTHS = [340, 450, 600, 800, 1050];
  const CARD_SIZES = '(min-width: 1719px) 520px, (min-width: 600px) 30vw, 45vw';

  function hpSrcset(url) {
    if (!url || typeof url !== 'string') return '';
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1) return '';
    if (/[?&]width=/.test(url)) return '';
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return CARD_IMAGE_WIDTHS.map(function (w) {
      return url + sep + 'width=' + w + ' ' + w + 'w';
    }).join(', ');
  }

  // Spaltenzahl des Boost-Grids (am Dev-Theme gemessen; nur für die Eager-Policy
  // der ersten Reihe — eine kleine Abweichung kostet höchstens ein eager Bild).
  function hpGridCols() {
    return window.matchMedia && window.matchMedia('(min-width: 600px)').matches ? 3 : 2;
  }

  // Position der Karte unter ihren Geschwister-Karten — reine DOM-Zählung, kein
  // Layout-Read (injectSkeleton läuft pre-paint im MutationObserver-Microtask).
  function hpGridIndex(itemEl) {
    let idx = 0;
    let n = itemEl;
    while ((n = n.previousElementSibling)) {
      if (n.classList && n.classList.contains('boost-sd__product-item')) idx++;
      if (idx > 8) break; // alles jenseits der ersten Reihe ist egal
    }
    return idx;
  }

  function hpNearViewport(img) {
    try {
      const r = img.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      return r.bottom > -300 && r.top < vh + 300;
    } catch (e) {
      return true;
    }
  }

  let hpLazyHealObserver = null;
  function hpLazyHeal(img) {
    if (!('IntersectionObserver' in window)) {
      img.removeAttribute('loading'); // kein Observer möglich → altes Verhalten
      return;
    }
    if (!hpLazyHealObserver) {
      hpLazyHealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          hpLazyHealObserver.unobserve(e.target);
          const im = e.target;
          im.dataset.hpHealFired = '1';
          if (im.complete && im.naturalWidth > 0) return; // normal geladen
          // Grey-Placeholder-Bug: lazy Bild nach src-Wechsel neu anstossen.
          const s = im.getAttribute('src');
          im.removeAttribute('loading');
          if (s) im.src = s;
        });
      }, { rootMargin: '300px' });
    }
    img.dataset.hpHealObserved = '1';
    hpLazyHealObserver.observe(img);
  }

  // Einziger erlaubter Weg, einer Karte ein Bild zu setzen.
  function hpApplyImage(img, rawUrl) {
    if (!img) return;
    hpPrepImg(img);
    const url = hpSized(rawUrl);
    if (!url) return;
    // srcset setzen, wenn das Bild damit geboren wurde ODER noch jungfräulich
    // ist (src="" — Bridge-Markup der alten customization.js). So wählt auch im
    // Push-Fenster das finale Bild denselben Kandidaten wie das Skeleton →
    // Cache-Treffer statt Zweit-Download. NICHT nachrüsten auf einem Bild, das
    // ohne srcset bereits lädt/geladen hat (Kandidaten-Wechsel = Doppel-Download).
    if (img.hasAttribute('srcset') || !img.getAttribute('src')) {
      const srcset = hpSrcset(rawUrl);
      if (srcset) {
        if (img.getAttribute('srcset') !== srcset) {
          img.setAttribute('sizes', CARD_SIZES);
          img.setAttribute('srcset', srcset);
        }
      } else if (img.hasAttribute('srcset')) {
        // Neue URL ist nicht srcset-fähig → altes srcset darf nicht gewinnen.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
    }
    if (img.getAttribute('src') === url) return; // byte-identisch → kein Reset
    if (img.getAttribute('loading') === 'lazy') {
      // KEINE Geometrie-Entscheidung hier: Während der Hydration ist das
      // Boost-Grid noch unsichtbar (gemessen 2026-08-15: rect.top=0 für ALLE
      // Karten) — Positions-Checks würden alles fälschlich als «im Viewport»
      // werten. Darum dieselbe DOM-Index-Regel wie im Skeleton: erste Reihe
      // eager, Rest bleibt lazy und wird per Self-Heal-Observer abgesichert.
      const item = img.closest ? img.closest('.boost-sd__product-item') : null;
      const firstRow = item ? hpGridIndex(item) < hpGridCols() : hpNearViewport(img);
      if (firstRow) img.removeAttribute('loading');
      else hpLazyHeal(img);
    }
    img.src = url;
  }

  // Formate-Regel: assets/hp-thumb-fit.js entscheidet pro Bild, ob es die Kachel
  // füllen darf (Lifestyle-Foto) oder unangetastet bleibt (Produktbild auf Weiss) —
  // dieselbe Regel wie bei den PDP-Thumbnails. Auf der Produktseite lädt das Theme
  // die Datei selbst; die Kategoriekarte wird von Boost gerendert, darum holen wir
  // sie hier nach. Der Pfad kommt aus der eigenen Script-URL, damit er in jedem
  // Theme-Slot (cdn/shop/t/NNN) stimmt.
  const HP_SELF_SRC = (document.currentScript && document.currentScript.src) || '';

  function hpEnsureThumbFit() {
    if (window.__hpThumbFitInstalled) return;
    if (document.querySelector('script[data-hp-thumb-fit]')) return;
    const base = HP_SELF_SRC.split('?')[0].replace(/hp-collection-card\.js$/, '');
    if (!base || base === HP_SELF_SRC.split('?')[0]) return;
    const s = document.createElement('script');
    s.src = base + 'hp-thumb-fit.js';
    s.defer = true;
    s.setAttribute('data-hp-thumb-fit', '');
    document.head.appendChild(s);
  }

  // Nach jedem Bildwechsel neu klassieren (debounced in hp-thumb-fit.js).
  function hpRefreshFit() {
    if (window.HPThumbFit && window.HPThumbFit.refresh) window.HPThumbFit.refresh();
  }

  // HIG-124: the delivery-label catalog (48 localized strings) + the lookup/
  // priority/bell/compact-render helpers now live ONCE in
  // assets/hp-delivery-core.js (window.HPDeliveryCore), shared by the card, the
  // PDP and the quick-add drawer. The thin wrappers below delegate to it.

  // Active locale from Shopify; fall back to German. Kept local because it's used
  // across the card (reviews / tag labels / CTA), not only for delivery.
  const ACTIVE_LOCALE = (function () {
    const sl = (window.Shopify && window.Shopify.locale) || 'de';
    const short = String(sl).toLowerCase().split('-')[0];
    return ({ de: 1, en: 1, fr: 1, it: 1 })[short] ? short : 'de';
  })();

  // Look up a label by key — delegates to the shared core.
  function lookupLabel(labelKey) {
    return window.HPDeliveryCore.lookupLabel(labelKey);
  }

  // Judge.me review-count phrasing per locale (singular vs plural).
  // Kept here rather than as Shopify locale strings so it lives alongside
  // the rest of this widget's translation table.
  const REVIEW_LABELS = {
    de: function (n) { return n === 1 ? '1 Bewertung' : n + ' Bewertungen'; },
    en: function (n) { return n === 1 ? '1 review'    : n + ' reviews'; },
    fr: function (n) { return n === 1 ? '1 avis'      : n + ' avis'; },
    it: function (n) { return n === 1 ? '1 recensione' : n + ' recensioni'; },
  };

  function formatReviewCount(n) {
    return (REVIEW_LABELS[ACTIVE_LOCALE] || REVIEW_LABELS.de)(n);
  }

  // Defensive: if Liquid pre-translation worked (data-hp-review-text contains
  // a digit = the count interpolated into the locale string), use it as-is.
  // If it looks like an untranslated key (e.g. "products.product.reviews_count"
  // — happens when Boost's Liquid scope can't resolve pluralization), fall
  // back to JS-side REVIEW_LABELS map. Verified 2026-05-31: Boost's Liquid
  // returns the raw key when `t:` can't find the .one/.other variant.
  function resolveReviewText(serverText, count) {
    if (serverText && /\d/.test(serverText)) return serverText;
    return formatReviewCount(count);
  }

  // HIG-132: split the count phrase into number + word so the card can show the
  // full "98 Bewertungen" on desktop but a compact "★★★★★ (98)" on mobile — CSS
  // hides the word + parenthesizes the number below 768px. Locale-agnostic: strips
  // the leading number/separators off the full phrase to get the word ("reviews",
  // "avis", "recensioni", "Bewertungen"). PDP reviews are untouched.
  function reviewCountHTML(count, fullText) {
    const word = String(fullText || '').replace(/^\s*\d[\d'’.,\s]*/, '').trim();
    return '<span class="hp-card__review-num">' + count + '</span>' +
      (word ? ' <span class="hp-card__review-word">' + escapeHtml(word) + '</span>' : '');
  }

  // Rating parser. The reviews.rating metafield is Shopify's "rating" type
  // which stores `{"value":"4.83","scale_min":"1.0","scale_max":"5.0"}` as
  // a JSON string. A plain parseFloat() on the raw value gives NaN. This
  // helper handles both the JSON-object form (current Judge.me / Shopify
  // standard) AND a plain numeric string (defensive — TEST_DATA, future
  // simpler metafield setups).
  function parseRating(s) {
    if (s == null || s === '') return 0;
    if (typeof s === 'number') return s;
    try {
      const v = JSON.parse(s);
      if (v && typeof v === 'object' && v.value != null) return parseFloat(v.value) || 0;
      return parseFloat(v) || 0;
    } catch (e) {
      return parseFloat(s) || 0;
    }
  }

  // Stars: 5 slots, filled by Math.round(rating). Unicode rather than SVG —
  // sharp at the card size we render and trivially CSS-coloured via
  // .hp-card__stars { color: var(--hp-card-star-color) }. Half-stars (4.5 →
  // 4 full + 1 half + 0 empty) would need SVG masks; Tobias OK to add later.
  function renderStars(rating) {
    const filled = Math.max(0, Math.min(5, Math.round(rating)));
    return '★'.repeat(filled) + '☆'.repeat(5 - filled);
  }

  // Convenience for TEST_DATA construction below.
  function entry(labelKey, qty) {
    return { label_key: labelKey, qty: qty };
  }

  const TEST_DATA = {
    // Card 1 — 2-option product (Color × Size). Tests tier 1 + tier 2.
    // Pre-selection target: L (priority 1, sales_score 150 beats M's 80).
    card1: {
      product_title: 'T-Shirt Premium',
      product_url: '/products/t-shirt-premium',
      group_option_name: 'color',
      group_value: 'Black',
      group_label: 'Schwarz',
      vendor: 'Velites',
      rating: 4.5,
      review_count: 24,
      dropdown_option_names: ['size'],
      option_labels: { color: 'Color', size: 'Size' },
      variants: [
        { id: 1101, options: { color: 'Black', size: 'XS' }, price: 4990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/tshirt-black.jpg' },
        { id: 1102, options: { color: 'Black', size: 'S' },  price: 4990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/tshirt-black.jpg' },
        { id: 1103, options: { color: 'Black', size: 'M' },  price: 4990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/tshirt-black.jpg' },
        { id: 1104, options: { color: 'Black', size: 'L' },  price: 4990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/tshirt-black.jpg' },
        { id: 1105, options: { color: 'Black', size: 'XL' }, price: 5490, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/tshirt-black.jpg' },
      ],
      variants_data: {
        1101: { stock_breakdown: [], sales_score: 5 },
        1102: { stock_breakdown: [entry('in_7_9_days', 4)], sales_score: 20 },
        1103: { stock_breakdown: [entry('instant', 6)],     sales_score: 80 },
        1104: { stock_breakdown: [entry('instant', 8)],     sales_score: 150 },
        1105: { stock_breakdown: [entry('in_7_9_days', 3)], sales_score: 12 },
      },
    },

    // Card 2 — 3-option product (Flavor × Size × Quantity). Tests tier 1 + tier 2 + OOS.
    // Pre-selection target: 1kg / 1 Stk (priority 1, sales_score 320 beats 500g/1Stk's 200).
    card2: {
      product_title: 'Whey Protein Vanilla',
      product_url: '/products/whey-protein-vanilla',
      group_option_name: 'flavor',
      group_value: 'Vanilla',
      group_label: 'Vanille',
      vendor: 'OptimumNutrition',
      rating: 4,
      review_count: 87,
      dropdown_option_names: ['size', 'quantity'],
      option_labels: { flavor: 'Flavor', size: 'Size', quantity: 'Quantity' },
      variants: [
        { id: 2201, options: { flavor: 'Vanilla', size: '500g', quantity: '1 Stk' }, price: 3990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/whey-vanilla.jpg' },
        { id: 2202, options: { flavor: 'Vanilla', size: '500g', quantity: '3 Stk' }, price: 10990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/whey-vanilla.jpg' },
        { id: 2203, options: { flavor: 'Vanilla', size: '1kg',  quantity: '1 Stk' }, price: 6990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/whey-vanilla.jpg' },
        { id: 2204, options: { flavor: 'Vanilla', size: '1kg',  quantity: '3 Stk' }, price: 18990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/whey-vanilla.jpg' },
        { id: 2205, options: { flavor: 'Vanilla', size: '2kg',  quantity: '1 Stk' }, price: 12990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/whey-vanilla.jpg' },
      ],
      variants_data: {
        2201: { stock_breakdown: [entry('instant', 12)],       sales_score: 200 },
        2202: { stock_breakdown: [entry('in_3_4_days', 5)],    sales_score: 60 },
        2203: { stock_breakdown: [entry('instant', 18)],       sales_score: 320 },
        2204: { stock_breakdown: [], sales_score: 30 },
        2205: { stock_breakdown: [entry('in_10_14_days', 4)], sales_score: 45 },
      },
    },

    // Card 3 — Tier-3 edge case: 1-option product, all in-stock, all priority 1,
    // all sales_score = 0. Pre-selection target: M (upper-middle of 5 → index 2).
    // Also: review_count=0 → reviews row is hidden entirely (no "0 Bewertungen" placeholder).
    card3: {
      product_title: 'Lifting Belt (Test Tier 3)',
      product_url: '/products/lifting-belt',
      group_option_name: 'color',
      group_value: 'Black',
      group_label: 'Schwarz',
      vendor: 'BrandX',
      rating: 0,
      review_count: 0,
      dropdown_option_names: ['size'],
      option_labels: { color: 'Color', size: 'Size' },
      variants: [
        { id: 3301, options: { color: 'Black', size: 'XS' }, price: 8990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/lifting-belt.jpg' },
        { id: 3302, options: { color: 'Black', size: 'S' },  price: 8990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/lifting-belt.jpg' },
        { id: 3303, options: { color: 'Black', size: 'M' },  price: 8990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/lifting-belt.jpg' },
        { id: 3304, options: { color: 'Black', size: 'L' },  price: 8990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/lifting-belt.jpg' },
        { id: 3305, options: { color: 'Black', size: 'XL' }, price: 8990, image: 'https://cdn.shopify.com/s/files/1/0552/2055/2962/files/lifting-belt.jpg' },
      ],
      variants_data: {
        3301: { stock_breakdown: [entry('instant', 3)], sales_score: 0 },
        3302: { stock_breakdown: [entry('instant', 4)], sales_score: 0 },
        3303: { stock_breakdown: [entry('instant', 2)], sales_score: 0 },
        3304: { stock_breakdown: [entry('instant', 5)], sales_score: 0 },
        3305: { stock_breakdown: [entry('instant', 6)], sales_score: 0 },
      },
    },
  };

  const SALES_SCORE_EPSILON = 0.5;

  // Gift card (Tobias 2026-06-26): a single digital gift-card product, identified by
  // product ID (data-hp-card). It carries no real stock_breakdown, so the card would
  // otherwise show "Lieferzeit unbekannt" + a Benachrichtigen button. Instead we render
  // the normal instant line ("Sofort versandfertig", localized via the shared catalog)
  // and keep the cart button. Decoupled from the warehouse data on purpose — matches the
  // PDP box (snippets/delivery-gift-card.liquid) and the cart property.
  var HP_GIFT_CARD_PRODUCT_IDS = ['7452375875814'];
  function hpIsGiftCardId(id) { return HP_GIFT_CARD_PRODUCT_IDS.indexOf(String(id)) !== -1; }
  function renderGiftCardDeliveryHTML() {
    var meta = (typeof lookupLabel === 'function' ? lookupLabel('instant') : null) || {};
    var label = meta.label_standalone || 'Sofort versandfertig';
    var icon = (window.HPDeliveryCore && window.HPDeliveryCore.checkSvg)
      ? window.HPDeliveryCore.checkSvg(meta.icon_color || '#4EA72E', 15) : '✓';
    return '<span class="hp-card__delivery-line" style="color:' + (meta.color || '#4EA72E') + '">' +
      '<span class="hp-avail__ic">' + icon + '</span><span>' + label + '</span></span>';
  }

  function getVariantData(data, variant) {
    return (variant && data.variants_data[variant.id]) || {
      stock_breakdown: [],
      sales_score: 0,
    };
  }

  // Derive priority from label_key via LABEL_CATALOG (locale-independent —
  // priority lives in the catalog, not in the per-product metafield).
  function priorityOf(breakdownEntry) {
    const meta = lookupLabel(breakdownEntry.label_key);
    return meta ? meta.priority : 999;
  }

  // True if the variant matches every pinned (fixed) option of the card.
  function matchesPins(variant, pins) {
    for (const k in pins) { if (variant.options[k] !== pins[k]) return false; }
    return true;
  }

  // The options NOT shown as dropdowns are FIXED to the card's representative
  // (Boost-split) variant. Generalizes the old single group_option_name to ANY
  // number of split options — e.g. a set split by BOTH "set-variante" AND
  // "hantelscheiben farbe", leaving only "langhantelgewicht" as a dropdown.
  // data.base_variant_id (the card's variant, passed by customization.js) supplies
  // the pinned values; without it we fall back to the legacy single
  // group_option_name/value so existing split cards keep working.
  function pinnedOptions(data) {
    const dropSet = {};
    (data.dropdown_option_names || []).forEach((n) => { dropSet[n] = true; });
    const base = data.base_variant_id
      ? data.variants.find((v) => String(v.id) === String(data.base_variant_id))
      : null;
    if (base && base.options) {
      const pins = {};
      for (const name in base.options) { if (!dropSet[name]) pins[name] = base.options[name]; }
      return pins;
    }
    return data.group_option_name ? { [data.group_option_name]: data.group_value } : {};
  }

  // Three-tier pre-selection. See WAREHOUSE_LOGIC.md §3.3.
  function pickInitialVariant(data) {
    const pins = pinnedOptions(data);
    const inGroup = data.variants.filter((v) => matchesPins(v, pins));
    const available = inGroup.filter(
      (v) => getVariantData(data, v).stock_breakdown.length > 0
    );
    if (available.length === 0) {
      return inGroup[0] || null;
    }

    // Tier 1 — fastest priority wins
    const minPriority = Math.min(
      ...available.map((v) => priorityOf(getVariantData(data, v).stock_breakdown[0]))
    );
    const tier1 = available.filter(
      (v) => priorityOf(getVariantData(data, v).stock_breakdown[0]) === minPriority
    );
    if (tier1.length === 1) return tier1[0];

    // Tier 2 — highest sales_score among tier-1 winners
    const maxScore = Math.max(...tier1.map((v) => getVariantData(data, v).sales_score));
    const tier2 = tier1.filter(
      (v) => getVariantData(data, v).sales_score >= maxScore - SALES_SCORE_EPSILON
    );
    if (tier2.length === 1) return tier2[0];

    // Tier 3 — upper-middle index when sales_scores are tied or all ~ 0.
    // tier2 preserves the original variant order (filter is stable), so the
    // visual middle of the candidate set is index floor(N/2).
    const midIdx = Math.floor(tier2.length / 2);
    return tier2[midIdx];
  }

  function findVariant(data, selection) {
    return (
      data.variants.find((v) => {
        for (const name of Object.keys(selection)) {
          if (v.options[name] !== selection[name]) return false;
        }
        return true;
      }) || null
    );
  }

  // HIG-90: reuse formatCHF so prices get the Swiss apostrophe thousands
  // separator (1'234.00). Previously this emitted "CHF 1234.00" (no separator),
  // which was inconsistent with the compare-at price (formatCHF) on the same card.
  function formatPrice(cents) {
    return formatCHF(cents / 100);
  }

  // HIG-129: keep the price + struck compare-at price on ONE line on narrow
  // cards. A 4-digit price next to a 4-digit compare overflows the ~148px mobile
  // card column at the base size and wraps the compare to a 2nd line. Shrink THIS
  // row's price + compare just enough to fit — only rows that actually overflow;
  // 3-digit prices keep full size. Re-run on every price change (variant switch).
  function fitPriceRow(cardEl) {
    const row = cardEl.querySelector('.hp-card__price-row');
    if (!row) return;
    const price = row.querySelector('.hp-card__price');
    const cmp = row.querySelector('.hp-card__compare');
    if (!price || !cmp) return;
    price.style.fontSize = '';
    cmp.style.fontSize = '';
    row.style.flexWrap = 'nowrap';
    // Force both to their natural single-line width so row.scrollWidth reflects
    // the TRUE content width — otherwise flex-shrink squeezes the price box and
    // wraps "CHF 2'299.00" internally to 2 lines, hiding the overflow from the
    // measurement (verified 2026-06-07).
    price.style.whiteSpace = 'nowrap';
    price.style.flex = '0 0 auto';
    cmp.style.flex = '0 0 auto';
    let size = parseFloat(getComputedStyle(price).fontSize) || 17;
    let guard = 0;
    while (row.scrollWidth > row.clientWidth + 1 && size > 9 && guard < 16) {
      size -= 1;
      price.style.fontSize = size + 'px';
      cmp.style.fontSize = (size * 0.85) + 'px';
      guard++;
    }
  }

  // HIG-39 lite cards take price_min straight from product-item.liquid's
  // `data-hp-price-min` (`product.price_min | money`). On Boost's CLIENT
  // re-render the money filter is dropped and the value comes back as a bare
  // major-unit number (e.g. "69.9") instead of "CHF 69.90" (verified
  // 2026-05-31). So: pass through anything that already contains a letter (a
  // currency symbol/code — correct SSR output), otherwise treat it as a
  // major-unit number and format it. money never emits bare cents, so a
  // major-unit assumption is safe for the broken-client case.
  function formatLitePrice(s) {
    if (!s) return '';
    if (/[a-zA-Z]/.test(s)) return s;
    var n = parseFloat(String(s).replace(',', '.'));
    return isNaN(n) ? String(s) : formatCHF(n); // HIG-90: apostrophe thousands sep
  }

  function uniqueValuesForOption(data, optionName) {
    const pins = pinnedOptions(data);
    const seen = new Set();
    const out = [];
    for (const v of data.variants) {
      if (!matchesPins(v, pins)) continue; // only values within this card's pinned set
      const val = v.options[optionName];
      if (seen.has(val)) continue;
      seen.add(val);
      out.push(val);
    }
    return out;
  }

  // Card data carries option names LOWERCASED (Boost lowercases them for variant
  // matching), so a dropdown label like "set-variante" must render with its
  // original-style capitalization ("Set-Variante"). Capitalize the first letter
  // after start / space / hyphen / slash. (HIG-61)
  function titleCaseOption(name) {
    return String(name).replace(/(^|[\s\-/])([a-zà-ÿ])/g, function (_, sep, ch) {
      return sep + ch.toUpperCase();
    });
  }

  function buildSelectors(cardEl, data) {
    const container = cardEl.querySelector('[data-hp-card-selectors]');
    container.innerHTML = '';

    for (const optionName of data.dropdown_option_names) {
      // Skip Shopify's default single-variant pseudo-option ("Title", whose only
      // value is "Default Title") — a 1-entry dropdown reading "Default Title" is
      // noise. Single-variant products then show just price/delivery/ATC, no select.
      // (Mirrors the title-pinning exclusion in hydrate(). Tobias 2026-06-06.)
      if (String(optionName).toLowerCase() === 'title') continue;
      const wrapper = document.createElement('label');
      wrapper.className = 'hp-card__selector';
      wrapper.setAttribute('data-hp-selector-option', optionName);

      const labelText = document.createElement('span');
      labelText.className = 'hp-card__selector-label';
      labelText.textContent = data.option_labels[optionName] || titleCaseOption(optionName);
      wrapper.appendChild(labelText);

      const select = document.createElement('select');
      select.className = 'hp-card__select';
      select.setAttribute('data-hp-select-option', optionName);

      const values = uniqueValuesForOption(data, optionName);
      for (const val of values) {
        const opt = document.createElement('option');
        opt.value = val;
        opt.textContent = val;
        select.appendChild(opt);
      }

      wrapper.appendChild(select);
      container.appendChild(wrapper);
    }
  }

  function readCurrentSelection(cardEl, data) {
    // Start from the card's pinned (fixed) options, then layer the dropdowns.
    const selection = Object.assign({}, pinnedOptions(data));
    cardEl.querySelectorAll('[data-hp-select-option]').forEach((s) => {
      selection[s.dataset.hpSelectOption] = s.value;
    });
    return selection;
  }

  // cart_property is computed at render time from the fastest breakdown
  // entry's label_key, in the active locale.
  function buildCartProperty(stockBreakdown) {
    if (!stockBreakdown || stockBreakdown.length === 0) return '';
    const meta = lookupLabel(stockBreakdown[0].label_key);
    return meta ? meta.icon + ' ' + meta.label_standalone : '';
  }

  // Per-bucket breakdown — compact card format ("<icon> <qty|10+> <label_lower>",
  // no unit word, 2 lines, OOS = bell + "Lieferzeit unbekannt"). HIG-124: the
  // implementation now lives in the shared core (used by the card AND the
  // quick-add drawer); this just delegates.
  function renderBreakdownHTML(stockBreakdown, opts) {
    return window.HPDeliveryCore.renderCompact(stockBreakdown, opts);
  }

  // Per-variant discount (FULL cards): the struck compare price + the badge
  // reflect the SELECTED variant's own compare_at_price, not the product-level
  // min. Critical for products whose variants vary a lot in price (e.g. a set
  // with Intro→Elite tiers) — the product-min struck price would otherwise show
  // up LOWER than a pricier selected variant. Pass variant=null to clear. Lite
  // cards keep the product-level applyDiscount (their price IS the product min).
  // Needs variant.compare_at (cents) from customization.js; absent → no discount
  // shown (strictly better than a wrong one). HIG-61.
  function updateVariantDiscount(cardEl, variant) {
    const media = cardEl.querySelector('.hp-card__media');
    const oldCmp = cardEl.querySelector('.hp-card__compare');
    if (oldCmp) oldCmp.remove();
    const oldBadge = media && media.querySelector('.hp-card__discount-badge');
    if (oldBadge) oldBadge.remove();

    const cmp = variant && variant.compare_at ? variant.compare_at : 0;
    if (!variant || !cmp || cmp <= variant.price) return;
    const pct = Math.round((1 - variant.price / cmp) * 100);
    if (pct <= 0) return;

    if (media) {
      const badge = document.createElement('span');
      badge.className = 'hp-card__discount-badge';
      badge.textContent = '-' + pct + '%';
      media.appendChild(badge);
    }
    const priceEl = cardEl.querySelector('[data-hp-card-price]');
    if (priceEl && priceEl.parentNode) {
      let row = priceEl.parentNode.classList.contains('hp-card__price-row') ? priceEl.parentNode : null;
      if (!row) {
        row = document.createElement('div');
        row.className = 'hp-card__price-row';
        priceEl.parentNode.insertBefore(row, priceEl);
        row.appendChild(priceEl);
      }
      const span = document.createElement('span');
      span.className = 'hp-card__compare';
      span.textContent = formatPrice(cmp);
      row.appendChild(span);
    }
  }

  function applyVariant(cardEl, data, variant) {
    const imgEl = cardEl.querySelector('[data-hp-card-image]');
    // Product-image fallback (Tobias 2026-06-06): single-variant products (e.g.
    // Chalk Bag) have no per-variant featured image, so variant.image is null —
    // fall back to the PRODUCT image (Boost's data-hp-image) instead of the
    // grey no-image.png placeholder.
    const productImg = cardEl.getAttribute('data-hp-image') || cardEl.getAttribute('data-hp-card-image') || '';
    const linkEl = cardEl.querySelector('[data-hp-card-link]');
    const priceEl = cardEl.querySelector('[data-hp-card-price]');
    const deliveryEl = cardEl.querySelector('[data-hp-card-delivery]');
    const atcBtn = cardEl.querySelector('[data-hp-card-atc]');
    const notifyBtn = cardEl.querySelector('[data-hp-card-notify]');
    const reviewsLink = cardEl.querySelector('[data-hp-card-reviews]');

    // Localize the CTA button labels (customization.js renders them in German).
    if (atcBtn) { const t = ctaLabel('add'); if (atcBtn.textContent.trim() !== t) atcBtn.textContent = t; }
    if (notifyBtn) { const t = ctaLabel('notify'); if (notifyBtn.textContent.trim() !== t) notifyBtn.textContent = t; }

    // Keep the reviews link pointing at the current variant's PDP.
    if (reviewsLink && reviewsLink.tagName === 'A' && data && data.product_url) {
      const variantQs = variant && variant.id ? '?variant=' + variant.id : '';
      reviewsLink.href = data.product_url + variantQs + '#judgeme_product_reviews';
    }

    // Bild-Handling (Loading-Policy, srcset, byte-identischer src) läuft zentral
    // über hpApplyImage — ersetzt das frühere pauschale removeAttribute('loading').

    // Auslaufend-Flag (variants_data._disc, vom HPB-Sync aus dem Produkt-Tag
    // «auslaufend» geschrieben): ausverkaufte Varianten zeigen «Ausverkauft» +
    // stillen grauen Knopf statt Benachrichtigen (Tobias 2026-07-30).
    const isDiscontinued = !!(data && data.variants_data && data.variants_data._disc === 1);
    function applySoldOutCta() {
      if (isDiscontinued) {
        atcBtn.hidden = true;
        notifyBtn.hidden = false;
        notifyBtn.textContent = ctaLabel('soldout');
        notifyBtn.disabled = true;
        notifyBtn.classList.add('hp-card__cta--soldout');
      } else {
        atcBtn.hidden = true;
        notifyBtn.hidden = false;
        notifyBtn.disabled = false;
        notifyBtn.classList.remove('hp-card__cta--soldout');
        notifyBtn.textContent = ctaLabel('notify');
      }
    }
    if (!variant) {
      hpApplyImage(imgEl, productImg || FALLBACK_IMAGE);
      priceEl.textContent = '';
      deliveryEl.innerHTML = renderBreakdownHTML([], { discontinued: isDiscontinued });
      deliveryEl.style.color = '';
      applySoldOutCta();
      cardEl.dataset.hpCardVariant = '';
      linkEl.href = data.product_url;
      updateVariantDiscount(cardEl, null);
      return;
    }

    const vd = getVariantData(data, variant);

    hpApplyImage(imgEl, variant.image || productImg || FALLBACK_IMAGE);
    hpRefreshFit();
    imgEl.alt = data.product_title;
    linkEl.href = data.product_url + '?variant=' + variant.id;
    syncBoostVariant(cardEl, variant.id); // Boost navigiert selbst — Daten mitziehen
    priceEl.textContent = formatPrice(variant.price);
    updateVariantDiscount(cardEl, variant);
    cardEl.dataset.hpCardVariant = String(variant.id);

    if (data.is_gift_card) {
      // Gift card: digital / instant — show the normal instant line (no quantity)
      // and the cart button, never the "unbekannt" + Benachrichtigen path.
      deliveryEl.innerHTML = renderGiftCardDeliveryHTML();
      deliveryEl.style.color = '';
      atcBtn.hidden = false;
      notifyBtn.hidden = true;
    } else if (vd.stock_breakdown.length === 0) {
      deliveryEl.innerHTML = renderBreakdownHTML([], { discontinued: isDiscontinued });
      deliveryEl.style.color = '';
      applySoldOutCta();
    } else {
      // Full qty-prefixed multi-line breakdown ("<icon> <qty> Stück <label_lower>").
      deliveryEl.innerHTML = renderBreakdownHTML(vd.stock_breakdown);
      deliveryEl.style.color = '';
      atcBtn.hidden = false;
      notifyBtn.hidden = true;
    }

    fitPriceRow(cardEl); // HIG-129: keep price + compare on one line
  }

  function onSelectionChange(cardEl, data) {
    const sel = readCurrentSelection(cardEl, data);
    const v = findVariant(data, sel);
    applyVariant(cardEl, data, v);
  }

  // HIG-8 (2026-05-31): real /cart/add wiring + cart drawer refresh.
  // Strategy A: a single Lieferzeit property = the fastest bucket label.
  // Stock-splitting (multiple properties per item) is Phase 2.7 — see
  // WAREHOUSE_LOGIC.md §9.1.
  function onAtcClick(cardEl, data) {
    const variantId = cardEl.dataset.hpCardVariant;
    if (!variantId) return;
    const variant = data.variants.find((x) => String(x.id) === variantId);
    if (!variant) return;
    const vd = getVariantData(data, variant);
    const cartProperty = buildCartProperty(vd.stock_breakdown);

    const atcBtn = cardEl.querySelector('[data-hp-card-atc]');
    if (atcBtn) atcBtn.disabled = true;

    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = rootUrl + 'cart/add.js';

    // HIG-122 (Option C): the delivery-time line-item property is set by the cart
    // annotator (warehouse-cart-reconcile.js) — the single author of `Lieferzeit`
    // — so the card just adds the item; the property appears once the annotator
    // runs (debounced, ~400ms after the add).
    //
    // We mirror the THEME'S OWN add flow (product-form.js): request the rendered cart
    // sections with the add, then feed them to the drawer's renderContents(). The old
    // path called drawer.refresh() → cart-items.refresh(), which threw
    // "cartItems.refresh is not a function" on collection pages because cart-items.js
    // isn't loaded there — so the drawer opened empty even though the add succeeded.
    // renderContents() lazy-loads cart-items.js and renders straight from the response,
    // so it works the first time and on every page (HIG cart-empty fix, 2026-06-09).
    const drawer = document.querySelector('cart-drawer');
    let sections = 'cart-icon-bubble';
    const drawerSection = drawer && drawer.closest('.shopify-section');
    if (drawerSection) sections += ',' + drawerSection.id.replace('shopify-section-', '');

    const body = {
      items: [{
        id: variant.id,
        quantity: 1,
        properties: {},
      }],
      sections: sections,
      sections_url: window.location.pathname,
    };

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json().then((j) => ({ status: r.status, body: j })))
      .then(({ status, body: result }) => {
        if (status >= 400 || result.status === 'bad_request' || result.errors) {
          console.warn('[hp-card] cart/add rejected', result);
          return;
        }
        // Render the drawer from the sections returned with the add (loads cart-items.js
        // if needed) and open it. Falls back to a plain open() if anything's missing.
        if (drawer && typeof drawer.renderContents === 'function' && result.sections) {
          drawer.renderContents(result, true);
        } else if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      })
      .catch((err) => {
        console.error('[hp-card] cart/add error', err);
      })
      .finally(() => {
        if (atcBtn) atcBtn.disabled = false;
      });
  }

  // Notify-Me: open STOQ / RestockRocket's back-in-stock popup directly via its
  // documented JS API window._RestockRocket.openModal(productData, variantId) —
  // the SAME entry point the quick-add drawer uses (HIG-106, assets/quick-add.js).
  // openModal needs the full Shopify product object, so we fetch /products/<handle>.js
  // (browser-cached) and pass it. Falls back to the PDP (with the variant
  // pre-selected, where STOQ renders its own button) when STOQ isn't loaded —
  // e.g. on the dev preview, where the app returns 401 (so this can only be fully
  // verified on production). Tobias 2026-06-06.
  function onNotifyClick(cardEl, data) {
    const variantId = cardEl.dataset.hpCardVariant;
    if (!variantId) return;
    const fallback = function () {
      if (data && data.product_url) window.location.href = data.product_url + '?variant=' + variantId;
    };
    const rr = window._RestockRocket;
    if (!rr || typeof rr.openModal !== 'function') { fallback(); return; }
    // HIG-155: prefer the canonical handle (locale-neutral) → fetch on the .ch root always
    // returns 200. data.product_url may now be the localized URL (…-en/…-it), whose handle
    // 404s on the .ch root. STOQ only needs variant ids (locale-neutral), so canonical is correct.
    const handle = (data && data.handle) ? data.handle
      : (data && data.product_url ? data.product_url : '').split('/products/').pop().split('?')[0];
    if (!handle) { fallback(); return; }
    if (!window.HP_NOTIFY_CACHE) window.HP_NOTIFY_CACHE = {};
    // STOQ's openModal matches the variant id STRICTLY (===) against the numeric
    // product.variants[].id. A string (dataset values are always strings) never matches
    // → STOQ silently falls back to the FIRST variant (e.g. shows "Black / S" when the
    // card had XL selected). Pass a Number so the right variant is pre-selected.
    // Verified on production 2026-06-09: Number → correct variant, String → first variant.
    const variantIdNum = Number(variantId);
    const openWith = function (product) {
      if (product) { try { rr.openModal(product, variantIdNum); return; } catch (e) {} }
      fallback();
    };
    if (window.HP_NOTIFY_CACHE[handle]) { openWith(window.HP_NOTIFY_CACHE[handle]); return; }
    fetch('https://highpowered.ch/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) window.HP_NOTIFY_CACHE[handle] = p; openWith(p); })
      .catch(function () { fallback(); });
  }

  // Tolerant JSON parser: handles undefined, "null", malformed JSON, and
  // wrong-shape values by returning the fallback. Critical because Boost's
  // client-side Liquid engine may emit empty/null metafield values for
  // products that don't have the logistics metafield set.
  function parseJsonAttr(s, fallback) {
    if (s == null || s === '' || s === 'null' || s === 'undefined') return fallback;
    try {
      const v = JSON.parse(s);
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  // Read card data from data-* attributes (Boost-rendered cards). Returns
  // an object in the same shape as a TEST_DATA entry, so the rest of the
  // controller doesn't care about the source.
  function readCardDataFromAttrs(cardEl) {
    const ds = cardEl.dataset;
    const variants = parseJsonAttr(ds.hpCardVariants, []);
    const variants_data = parseJsonAttr(ds.hpCardVariantsData, {});
    const dropdown_option_names = parseJsonAttr(ds.hpCardDropdownOptionNames, []);
    return {
      is_gift_card: hpIsGiftCardId(ds.hpCard),
      product_title: ds.hpCardProductTitle || '',
      product_url: ds.hpCardProductUrl || '',
      // HIG-155: canonical handle (locale-neutral) for .ch-root .js fetches (STOQ + carousel).
      // product_url may now be the localized URL (…-en/…-it) whose handle 404s on the .ch root,
      // so background fetches must use this canonical handle instead of deriving from product_url.
      handle: ds.hpCardHandle || '',
      group_option_name: ds.hpCardGroupOptionName || '',
      group_value: ds.hpCardGroupValue || '',
      group_label: ds.hpCardGroupValue || '',
      // HIG-61: the card's representative (Boost-split) variant id — supplied by
      // customization.js so the card can pin ALL non-dropdown options to it.
      base_variant_id: ds.hpCardBaseVariantId || '',
      dropdown_option_names: Array.isArray(dropdown_option_names) ? dropdown_option_names : [],
      option_labels: {},
      variants: Array.isArray(variants) ? variants : [],
      variants_data: (variants_data && typeof variants_data === 'object' && !Array.isArray(variants_data)) ? variants_data : {},
      vendor: ds.hpCardVendor || '',
      rating: parseRating(ds.hpCardRating),
      review_count: parseInt(ds.hpCardReviewCount || '0', 10) || 0,
      // Pre-translated count phrase from Liquid (e.g. "109 Bewertungen"); when
      // present, hydrate uses it as-is — no JS-side singular/plural needed.
      review_text: ds.hpCardReviewText || '',
      // HIG-39 lite-card fields: used only when variants_data is absent (the
      // full card derives image + price from the picked variant instead).
      // image is a URL string; price_min is a pre-formatted money string from
      // Liquid (product.price_min | money), so no JS-side currency formatting.
      image: ds.hpCardImage || '',
      price_min: ds.hpCardPriceMin || '',
      // HIG-39 lite split-variant cards: Boost shows one card per variant but
      // emits the bare product URL (no ?variant=). The specific variant id lives
      // in the data-product JSON (variantId, set when splitProduct). We carry it
      // so the lite card's PDP link can pre-select the clicked variant — full
      // cards get this from the picked variant in applyVariant. (HIG-64)
      split_variant_id: (function () {
        var raw = ds.product;
        if (!raw) return '';
        try {
          var o = JSON.parse(raw.replace(/&quot;/g, '"'));
          return o && o.splitProduct && o.variantId ? String(o.variantId) : '';
        } catch (e) { return ''; }
      })(),
    };
  }

  // True if a Boost-rendered card has enough data to actually hydrate.
  // Boost's client-side Liquid engine emits our wrapper for EVERY product
  // even when product.metafields.logistics.* are absent (verified
  // 2026-05-23). Such cards have empty variants/variants_data and must be
  // skipped — otherwise the user sees an empty card slot.
  function hasUsableData(data) {
    // group_option_name is OPTIONAL: a product with no split-option (e.g. a
    // T-shirt sized only) renders ONE full card with all options as dropdowns.
    // The full card needs only variants + variants_data; the group filter is
    // skipped downstream when group_option_name is absent.
    return data
      && data.variants.length > 0
      && Object.keys(data.variants_data).length > 0;
  }

  function getCardData(cardEl) {
    const cardId = cardEl.dataset.hpCard;
    // PoC fallback path: hardcoded TEST_DATA for the demo cards in
    // sections/hp-poc-card.liquid. Real Boost-rendered cards use a
    // numeric product ID and read from data-* attributes.
    if (TEST_DATA[cardId]) return TEST_DATA[cardId];
    if (cardEl.dataset.hpCardVariants || cardEl.dataset.hpCardProductTitle) {
      // HIG-39 (100% rollout): return whatever we have and let hydrate() decide
      // full vs lite via hasUsableData(). Lite cards (no variants_data) still
      // render static product info instead of being hidden.
      return readCardDataFromAttrs(cardEl);
    }
    return null;
  }

  // --- Boosts eigene Umleitung mit der richtigen Variante versorgen (2026-08-02) ---
  //
  // Boost (filter.module.js, FilterHandler.handleCoreClick) fängt JEDEN Klick
  // innerhalb `.boost-sd__product-item` ab, ruft `preventDefault()` und navigiert
  // SELBST via `window.location.href`. Unser `<a href="…?variant=…">` wird dabei
  // komplett ignoriert — gemessen 2026-08-01, unverändert seit Boost 1.76.0.
  // Boosts URL-Bauer (`buildProductDetailUrlWithVariant`) hängt `?variant=` nur an,
  // wenn im `data-product`-JSON BEIDES steht: `splitProduct` UND ein `variants`-
  // Schlüssel. Unser Boost-`product-item.liquid` liefert `variantId` + `splitProduct`,
  // aber KEIN `variants` → Boost landete auf der nackten Produkt-URL und die PDP
  // wählte die erste Variante. Symptom: Klick auf die Karte «Pink» öffnete «Schwarz».
  //
  // Wir nehmen Boost den Klick NICHT weg (weder `data-no-redirect` noch
  // `stopPropagation`), sondern korrigieren nur die DATEN, die Boost liest.
  // Grund: Boost schreibt im selben Zug sein «Zurück zur Position»-Tracking
  // (`boostSDSelectedProductId` / `-Page` / `-CurrentCollectionSelected`). Wer den
  // Klick abfängt, verliert das (so verhält sich heute schon der Bewertungs-Link).
  //
  // `variants` enthält bewusst nur die gewählte Variante: Boost prüft den Schlüssel
  // ausschliesslich auf Wahrheitswert, und die volle Liste × ~36 Karten wäre
  // unnötiges Seitengewicht. `splitProduct` wird gesetzt, weil Boost sonst auch bei
  // Nicht-Split-Karten mit Dropdown kein `?variant=` anhängt; im Boost-Quellcode
  // 1.88.2 kommen `splitProduct`/`split_product` NUR in den beiden URL-Bauern vor
  // (verifiziert 2026-08-02), die Flagge hat also keine anderen Nebenwirkungen.
  //
  // Siehe docs/COLLECTION_CARD.md §«Varianten-Vorauswahl beim Kartenklick».
  function syncBoostVariant(cardEl, variantId) {
    if (!cardEl || !variantId) return;
    var raw = cardEl.getAttribute('data-product');
    if (!raw) return;
    var obj;
    try { obj = JSON.parse(raw.replace(/&quot;/g, '"')); } catch (e) { return; }
    if (!obj || typeof obj !== 'object') return;
    // Schon aktuell → nicht bei jedem Render neu serialisieren.
    if (String(obj.variantId) === String(variantId) && obj.variants && obj.splitProduct) return;
    obj.variantId = String(variantId);
    // Eine ECHTE Variantenliste von Boost niemals überschreiben: Sie erscheint,
    // sobald in Boost der «Optionen wählen»-Knopf auf `quickAddToCart` steht
    // (siehe Ursache in docs/COLLECTION_CARD.md). Dann brauchen wir nichts zu
    // ergänzen — Boost prüft den Schlüssel ohnehin nur auf Wahrheitswert.
    if (!Array.isArray(obj.variants) || obj.variants.length === 0) {
      obj.variants = [{ id: Number(variantId) }];
    }
    obj.splitProduct = 'true';
    try { cardEl.setAttribute('data-product', JSON.stringify(obj)); } catch (e) { /* no-op */ }
  }

  // Whole-card click: anywhere on the card (except interactive children)
  // navigates to the variant's PDP. Mirrors the live shop UX where the entire
  // collection card is clickable. Children with their own handlers — selects,
  // buttons, the reviews link — call ev.stopPropagation() so they handle clicks
  // first (the closest() check below is a defensive backup, not the primary
  // mechanism — see memory reference_click_exclusion_stop_propagation).
  // Idempotent via the data flag. Shared by the full + lite render paths.
  function attachWholeCardClick(cardEl) {
    if (cardEl.dataset.hpCardClickAttached) return;
    cardEl.dataset.hpCardClickAttached = '1';
    cardEl.addEventListener('click', function (ev) {
      if (ev.target.closest(
        'button, select, [data-hp-select-option], [data-hp-card-reviews], [data-hp-card-atc], [data-hp-card-notify]'
      )) return;
      const link = cardEl.querySelector('[data-hp-card-link]');
      if (!link) return;
      const imLink = ev.target === link || link.contains(ev.target);

      // Cmd/Ctrl-Klick («in neuem Tab öffnen») und Shift-Klick («neues Fenster»),
      // 2026-08-02. Boost kennt KEINE Modifier-Tasten — im ganzen Bundle 1.88.2
      // kommt `metaKey`/`ctrlKey` nur in einem Zahlenfeld-Filter vor. Boosts
      // `preventDefault()` würgt darum die Neuer-Tab-Funktion des Browsers ab und
      // navigiert den AKTUELLEN Tab. Wir fangen genau diesen Fall ab, bevor er
      // Boost erreicht:
      //   · Klick IM Link (Bild): `stopPropagation` genügt, der Browser macht den
      //     neuen Tab von selbst — genau so verhält sich heute schon der
      //     Bewertungs-Link, der ebenfalls stopPropagation ruft.
      //   · Klick DANEBEN (Titel, Preis, Marke): es gibt keinen Link, den der
      //     Browser öffnen könnte, also öffnen wir ihn selbst.
      // Boosts «Zurück zur Position»-Tracking entfällt hier bewusst: Die
      // Kategorieseite bleibt ja im aktuellen Tab stehen, es gibt kein Zurück.
      // Mittelklick braucht nichts: der feuert `auxclick`, dem Boost gar nicht
      // zuhört, und wird auf dem Bild nativ abgewickelt.
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey) {
        ev.stopPropagation();
        if (!imLink) window.open(link.href, '_blank');
        return;
      }

      // Normaler Klick: unverändert. KEIN stopPropagation, damit Boost seinen
      // Lauf nimmt (Navigation + «Zurück zur Position»-Tracking) — die richtige
      // Variante bekommt Boost über syncBoostVariant() oben.
      if (!imLink) link.click();
    });
  }

  // HIG-36: render the discount badge ("-X%", top-left on the image) + the
  // struck-through compare-at price next to the sale price. The compare/price
  // come from the product-level cents that customization.js copies out of Boost's
  // data-product JSON onto data-hp-card-compare-cents / -price-cents. No-op when
  // the product isn't on sale. Idempotent (safe to call on every re-render).
  // Swiss money format to match `| money` (e.g. 1554 -> "CHF 1'554.00").
  function formatCHF(amount) {
    const parts = amount.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'"); // Swiss thousands sep
    return 'CHF ' + parts[0] + '.' + parts[1];
  }

  function applyDiscount(cardEl) {
    // data-hp-card-compare-amt / -price-amt are the product-level compare/sale
    // amounts copied by customization.js out of Boost's data-product JSON. NOTE:
    // Boost emits these in MAJOR units (e.g. "554", "399" = CHF 554 / CHF 399),
    // NOT cents — so parse as float and format directly (don't /100).
    const compare = parseFloat(String(cardEl.getAttribute('data-hp-card-compare-amt') || '').replace(',', '.'));
    const price = parseFloat(String(cardEl.getAttribute('data-hp-card-price-amt') || '').replace(',', '.'));
    if (!compare || !price || isNaN(compare) || isNaN(price) || compare <= price) return;
    const pct = Math.round((1 - price / compare) * 100);
    if (pct <= 0) return;

    // Badge on the image.
    const media = cardEl.querySelector('.hp-card__media');
    if (media && !media.querySelector('.hp-card__discount-badge')) {
      const badge = document.createElement('span');
      badge.className = 'hp-card__discount-badge';
      badge.textContent = '-' + pct + '%';
      media.appendChild(badge);
    }

    // Struck compare price on the same line as the (sale) price. The price text
    // is set via textContent by applyVariant/lite-path, so the compare must be a
    // SIBLING — wrap both in a flex row so they sit on one line.
    const priceEl = cardEl.querySelector('[data-hp-card-price]');
    if (priceEl && priceEl.parentNode && !priceEl.parentNode.classList.contains('hp-card__price-row')) {
      const row = document.createElement('div');
      row.className = 'hp-card__price-row';
      priceEl.parentNode.insertBefore(row, priceEl);
      row.appendChild(priceEl);
      const cmp = document.createElement('span');
      cmp.className = 'hp-card__compare';
      cmp.textContent = formatCHF(compare);
      row.appendChild(cmp);
    }
  }

  // HIG-36 Teil A: render Boost's tag-driven labels ("NEU", "BESTSELLER", …)
  // top-right on the image. Boost normally renders these itself via its
  // "custom-label-by-tag" feature, but our skeleton overwrites the product-item's
  // innerHTML so they get wiped — we re-render them here. customization.js mirrors
  // every `pfs:label-<TEXT>` tag from Boost's data-product JSON onto
  // data-hp-card-labels (comma-separated, e.g. "NEU,BESTSELLER"). Top-RIGHT so it
  // never collides with the top-left discount badge (mirrors Boost's own layout).
  // Idempotent. Note: label text is the raw tag suffix (German on the .ch shop);
  // EN/FR/IT translation is the separate HIG-26 follow-up.
  // Tag-driven labels (e.g. NEU) arrive from Boost as the raw German tag suffix.
  // Translation apps (T-Lab) can't reach JS-rendered text, so translate here by
  // active locale. Currently only NEU; add keys as new label-tags are introduced.
  const LABEL_TRANSLATIONS = {
    NEU: { de: 'NEU', en: 'NEW', fr: 'NOUVEAU', it: 'NUOVO' },
  };
  function translateLabel(raw) {
    const entry = LABEL_TRANSLATIONS[String(raw).trim().toUpperCase()];
    return entry ? (entry[ACTIVE_LOCALE] || entry.de || raw) : raw;
  }

  // CTA button text per locale (Tobias 2026-06-06): the card's add-to-cart and
  // notify buttons are JS-rendered (customization.js hardcodes German), so T-Lab
  // can't translate them — we localize here, same pattern as the NEU label. The
  // add-to-cart strings mirror the theme's `products.product.add_to_cart`. The
  // notify strings should match the PDP STOQ button (verify on production —
  // STOQ's text is an app setting; these are sensible defaults).
  const CTA_LABELS = {
    add:    { de: 'In den Warenkorb', en: 'Add to cart', fr: 'Ajouter au panier', it: 'Aggiungi al carrello' },
    // HIG-125: short label so the notify button stays ONE line at the cart
    // button's font-size → identical height + font (only the fill differs). The
    // bell icon + "Lieferzeit unbekannt" line already say "when available".
    notify: { de: 'Benachrichtigen', en: 'Notify me', fr: 'Prévenez-moi', it: 'Avvisami' },
    // Auslaufend (Tag «auslaufend», Tobias 2026-07-30): stiller grauer Knopf
    // statt Benachrichtigen — es kommt kein Nachschub mehr.
    soldout: { de: 'Ausverkauft', en: 'Sold out', fr: 'Épuisé', it: 'Esaurito' },
  };
  function ctaLabel(key) { return CTA_LABELS[key][ACTIVE_LOCALE] || CTA_LABELS[key].de; }

  function applyLabels(cardEl) {
    const raw = cardEl.getAttribute('data-hp-card-labels') || '';
    const labels = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!labels.length) return;
    const media = cardEl.querySelector('.hp-card__media');
    if (!media || media.querySelector('.hp-card__tag-labels')) return;
    const wrap = document.createElement('div');
    wrap.className = 'hp-card__tag-labels';
    labels.forEach(function (text) {
      const span = document.createElement('span');
      span.className = 'hp-card__tag-label';
      span.textContent = translateLabel(text);
      wrap.appendChild(span);
    });
    media.appendChild(wrap);
  }

  // ===== HIG-42: per-variant image carousel (hover arrows desktop / swipe mobile) ====
  // Lazy-loads /products/<handle>.js on first hover (desktop) or touch (mobile) — at
  // most one fetch per card the user actually interacts with, no upfront ~400-fetch
  // cost. Builds the image set for THIS card's colour group using the same
  // featured_image.position + variant_ids + sticky-inheritance logic as the PDP
  // variant-image filter (VARIANT_IMAGE_FILTER.md): images between one variant's
  // featured image and the next belong to that colour. A variant's slides = its own
  // images + the shared pre-variant shots (HIG-80 — same as the PDP, so a variant
  // with a single own image still shows the shared photos instead of collapsing to
  // one slide); the title image is excluded once a variant image shows; foreign-
  // variant images are excluded. No-op (no arrows) when fewer than 2 slides result.
  function hpProductCacheGet(handle) {
    window.HP_CAROUSEL_CACHE = window.HP_CAROUSEL_CACHE || {};
    if (Object.prototype.hasOwnProperty.call(window.HP_CAROUSEL_CACHE, handle)) {
      return Promise.resolve(window.HP_CAROUSEL_CACHE[handle]);
    }
    // Absolute live URL: Shopify's dev-preview proxy 401s on /products/X.js, the live
    // storefront returns 200 + CORS-permits it (same pattern as customization.js).
    return fetch('https://highpowered.ch/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { window.HP_CAROUSEL_CACHE[handle] = p || null; return p || null; })
      .catch(function () { window.HP_CAROUSEL_CACHE[handle] = null; return null; });
  }

  // Normalise a CDN image URL to its filename stem so the card's displayed src
  // (which may carry a ?width= transform) matches product.js media src (?v=…).
  function hpImageStem(src) {
    if (!src) return '';
    var s = String(src).split('?')[0];
    s = s.substring(s.lastIndexOf('/') + 1);
    s = s.replace(/\.(jpe?g|png|webp|gif|avif)$/i, '');
    s = s.replace(/_(\d+)?x(\d+)?(@\d+x)?$/i, ''); // Shopify size suffix _600x, _600x600
    return s;
  }

  function hpBuildImageGroup(product, displayedSrc) {
    if (!product) return null;
    var media = (product.media || [])
      .filter(function (m) { return m.media_type === 'image' && m.src; })
      .slice()
      .sort(function (a, b) { return a.position - b.position; });
    if (media.length < 2) return null;

    // images that are some variant's featured image (= start of a colour run).
    // Match by src STEM, not by .position or .id: featured_image.position is in
    // images-only coordinates while media.position counts videos/3D too (they drift
    // apart as soon as a product has non-image media — e.g. Quad Competition Grips has
    // a video at media-position 9, so Brute's featured image lands on images-position 9
    // = media-position 10 and a position match silently fails). featured_image.id also
    // lives in a different namespace than media.id. The src stem is the only
    // coordinate-independent key (same one we use to locate the displayed image).
    var featuredStems = {};
    (product.variants || []).forEach(function (v) {
      var fi = v.featured_image;
      if (fi && fi.src) featuredStems[hpImageStem(fi.src)] = true;
    });

    // sticky walk: each image inherits the colour group of the last featured image
    // at-or-before it (group 0 = pre-variant images before the first featured one)
    var current = 0, groupOf = {};
    media.forEach(function (m) {
      if (featuredStems[hpImageStem(m.src)]) current = m.position;
      groupOf[m.position] = current;
    });

    // The title image (product.featured_image) is shown as the card thumbnail and,
    // like on the PDP, is hidden once a variant image shows — so it is never paired
    // as a separate slide next to a variant image.
    var titleRaw = (typeof product.featured_image === 'string')
      ? product.featured_image
      : (product.featured_image && product.featured_image.src);
    var titleStem = hpImageStem(titleRaw);

    // Shared / pre-variant images (group 0, excluding the title). On the PDP these
    // stay visible for EVERY variant (see VARIANT_IMAGE_FILTER.md: pre-variant images
    // are shared shots). The card carousel must mirror that — otherwise a variant
    // that owns only its single featured image collapses to one slide (no carousel),
    // even though the product has shared shots the PDP would show. (HIG-80)
    var sharedExtras = media.filter(function (m) {
      return groupOf[m.position] === 0 && hpImageStem(m.src) !== titleStem;
    });

    // locate the displayed (card) image
    var dispStem = hpImageStem(displayedSrc), dispPos = null;
    for (var i = 0; i < media.length; i++) {
      if (hpImageStem(media[i].src) === dispStem) { dispPos = media[i].position; break; }
    }
    if (dispPos == null) dispPos = media[0].position;
    var dispGroup = groupOf[dispPos];

    // Build the ordered slide set, mirroring the PDP filter:
    //  - showing a variant image → that variant's own images + the shared shots
    //    (own images first, shared appended at the end, exactly like order:100 on PDP)
    //  - showing the title/shared image → the whole shared group (group 0)
    var ordered;
    if (dispGroup === 0) {
      ordered = media.filter(function (m) { return groupOf[m.position] === 0; });
      // lone title, no shared images, but variant runs exist → append the first
      // variant run so there's still something to browse. (Replaces an earlier branch
      // that referenced an undefined `featured` and threw, silently killing the
      // carousel — HIG-80.)
      if (ordered.length < 2) {
        var firstVariantPos = Infinity;
        media.forEach(function (m) {
          if (groupOf[m.position] !== 0 && m.position < firstVariantPos) firstVariantPos = m.position;
        });
        if (isFinite(firstVariantPos)) {
          var firstGroupId = groupOf[firstVariantPos];
          ordered = ordered.concat(media.filter(function (m) { return groupOf[m.position] === firstGroupId; }));
        }
      }
    } else {
      var ownGroup = media.filter(function (m) { return groupOf[m.position] === dispGroup; });
      ordered = ownGroup.concat(sharedExtras);
    }
    if (ordered.length < 2) return null;

    // order: displayed first (no duplicate), then the rest in the order built above
    var dispMedia = null;
    ordered.forEach(function (m) { if (m.position === dispPos) dispMedia = m; });
    if (!dispMedia) dispMedia = ordered[0];
    var srcs = [dispMedia.src];
    ordered.forEach(function (m) {
      if (m.src !== dispMedia.src && srcs.indexOf(m.src) === -1) srcs.push(m.src);
    });
    // Cap at 5 images (Tobias 2026-06-01): drop the trailing ones so the carousel
    // + the mobile dots never exceed 5. Displayed image stays first.
    if (srcs.length > 5) srcs = srcs.slice(0, 5);
    return srcs.length >= 2 ? srcs : null;
  }

  function setupCarousel(cardEl) {
    if (cardEl.dataset.hpCarouselSetup) return;
    cardEl.dataset.hpCarouselSetup = '1';
    var media = cardEl.querySelector('.hp-card__media');
    var img = cardEl.querySelector('[data-hp-card-image]');
    var link = cardEl.querySelector('[data-hp-card-link]');
    if (!media || !img) return;
    // HIG-155: prefer the canonical handle attr (locale-neutral) → carousel image .js
    // fetch on the .ch root always returns 200. The link href may now carry the localized
    // handle (…-en/…-it), which 404s on the .ch root; images are locale-neutral so canonical
    // is correct. Fall back to parsing the href for older cards without the attr.
    var handle = cardEl.dataset.hpCardHandle || '';
    if (!handle) {
      var href = (link && link.getAttribute('href')) || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      if (!m) return;
      handle = m[1];
    }

    var loaded = false, track = null, prevBtn = null, nextBtn = null, dotEls = [];

    // Same chevron the theme's slider arrows use (snippets/icon-chevron-left).
    // One glyph, rotated 180° for "next", so both arrows share the exact weight.
    var CHEVRON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"/></svg>';

    // Disable the arrow that points past the track edge — same behaviour as the
    // theme's <carousel-slider> (assets/main.js setNavButtonState): disabled arrow
    // stays visible but greyed + non-clickable (CSS opacity 0.4 + cursor default).
    function updateNavState() {
      if (!track || !prevBtn || !nextBtn) return;
      var max = track.scrollWidth - track.clientWidth;
      prevBtn.disabled = track.scrollLeft <= 1;
      nextBtn.disabled = track.scrollLeft >= max - 1;
    }

    // Pagination dots — the mobile affordance (no hover there for arrows). One dot
    // per slide (already capped at 5 in hpBuildImageGroup). Hidden on desktop via
    // CSS; the active dot follows the scroll position. (Tobias 2026-06-01, Baymard:
    // mobile carousels need a visible indicator.)
    function buildDots(count) {
      var wrap = document.createElement('div');
      wrap.className = 'hp-card__dots';
      for (var i = 0; i < count; i++) {
        var d = document.createElement('span');
        d.className = 'hp-card__dot' + (i === 0 ? ' is-active' : '');
        wrap.appendChild(d);
        dotEls.push(d);
      }
      media.appendChild(wrap);
    }
    function updateDots() {
      if (!dotEls.length || !track || !track.clientWidth) return;
      var idx = Math.round(track.scrollLeft / track.clientWidth);
      if (idx < 0) idx = 0;
      if (idx > dotEls.length - 1) idx = dotEls.length - 1;
      for (var i = 0; i < dotEls.length; i++) {
        dotEls[i].classList.toggle('is-active', i === idx);
      }
    }

    // Turn the single image into a horizontal scroll-snap track of slides, the same
    // native-scroll mechanism the theme's <carousel-slider> uses (assets/main.js):
    // the browser then handles finger-swipe (mobile) and two-finger-trackpad scroll
    // (desktop) for free, with momentum — far smoother than swapping img.src on click.
    // The arrows just scrollBy one slide for mouse users. media is an <a>, but native
    // scroll gestures don't fire a click, so tap = go to PDP, swipe = scroll (no guard).
    function injectArrows() {
      ['prev', 'next'].forEach(function (dir) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hp-card__nav hp-card__nav--' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? 'Vorheriges Bild' : 'Nächstes Bild');
        b.innerHTML = CHEVRON;
        b.addEventListener('click', function (ev) {
          ev.preventDefault(); ev.stopPropagation(); // media is an <a> — block PDP nav
          if (track) track.scrollBy({ left: (dir === 'prev' ? -1 : 1) * track.clientWidth, behavior: 'smooth' });
        });
        media.appendChild(b);
        if (dir === 'prev') prevBtn = b; else nextBtn = b;
      });
    }
    function makeSlide(imgEl) {
      var d = document.createElement('div');
      d.className = 'hp-card__slide';
      if (imgEl.parentNode) imgEl.parentNode.removeChild(imgEl);
      d.appendChild(imgEl);
      return d;
    }
    function buildTrack(srcs) {
      if (track) return;
      track = document.createElement('div');
      track.className = 'hp-card__track';
      media.insertBefore(track, media.firstChild);
      // slide 1 = the existing <img> (keeps its decoded pixels + data-hp-card-image).
      // Wrap every slide in a div — a not-yet-loaded <img> used directly as a flex
      // item collapses to 0 width (no scroll overflow → deadlock); a div doesn't.
      img.removeAttribute('loading');
      track.appendChild(makeSlide(img));
      for (var k = 1; k < srcs.length; k++) {
        var s = document.createElement('img');
        s.className = 'hp-card__image';
        hpPrepImg(s);
        // srcset VOR src (gleicher Builder wie Skeleton → konsistente Kandidaten).
        var slideSet = hpSrcset(srcs[k]);
        if (slideSet) { s.setAttribute('sizes', CARD_SIZES); s.setAttribute('srcset', slideSet); }
        s.src = hpSized(srcs[k]);
        s.alt = '';
        track.appendChild(makeSlide(s));
      }
      hpRefreshFit();
      injectArrows();
      buildDots(srcs.length);
      media.classList.add('hp-card__media--has-nav');
      // Toggle arrow disabled state + active dot as the track scrolls
      // (swipe, trackpad or arrow).
      track.addEventListener('scroll', function () { updateNavState(); updateDots(); }, { passive: true });
      // Initial state once layout has settled (slides need a frame to get width).
      requestAnimationFrame(function () { updateNavState(); updateDots(); });
    }
    function ensureLoaded() {
      if (loaded) return;
      loaded = true;
      hpProductCacheGet(handle).then(function (p) {
        var set = hpBuildImageGroup(p, img.getAttribute('src'));
        if (set) buildTrack(set);
      });
    }

    // Desktop: hover the card before interacting → load just-in-time.
    media.addEventListener('mouseenter', ensureLoaded);
    // Touch devices (no hover): prefetch + build as the card nears the viewport so the
    // FIRST swipe is already smooth (native scroll-snap needs the slides present).
    // One fetch per product, cached, only for cards actually scrolled to.
    if (window.matchMedia && window.matchMedia('(hover: none)').matches && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { ensureLoaded(); io.disconnect(); } });
      }, { rootMargin: '250px' });
      io.observe(cardEl);
    } else {
      media.addEventListener('touchstart', ensureLoaded, { passive: true, once: true });
    }
  }

  function hydrate(cardEl, dataOverride) {
    // dataOverride lets callers (e.g. customization.js after /products/X.js
    // fetch) provide the card data directly, skipping the data-* attribute
    // round-trip. Backward compat: if no override, falls back to TEST_DATA
    // or readCardDataFromAttrs via getCardData.
    const cardId = cardEl.dataset.hpCard;
    const data = dataOverride || getCardData(cardEl);
    if (!data) {
      // Hide empty cards that Boost's client renderer emitted via our
      // wrapper for products without the logistics metafields. Showing
      // them as empty card slots would break the grid visually.
      cardEl.style.display = 'none';
      return;
    }

    const titleEl = cardEl.querySelector('[data-hp-card-title]');
    const groupEl = cardEl.querySelector('[data-hp-card-group]');
    // HIG-9 Option B (Tobias 2026-05-30): inline the split value(s) into the
    // title — "Quad Ultra Grips (No Chalk) – Schwarz". Generalized 2026-06-06:
    // show ALL pinned (split) option values, not just the legacy single group
    // label — so a multi-split set reads "Langhantel Set – Advanced … – Olympic"
    // (the dropdown option is NOT pinned, so it's correctly excluded). The values
    // are the variant's own option values (localized with the card data).
    const pins = pinnedOptions(data);
    const pinValues = Object.keys(pins)
      // Skip Shopify's default option for single-variant products ("Title" =
      // "Default Title") so it never appears in the card title (Tobias 2026-06-06).
      .filter(function (name) { return String(name).toLowerCase() !== 'title'; })
      .map(function (name) { return pins[name]; })
      .filter(function (v) {
        var s = v == null ? '' : String(v).trim();
        return s !== '' && s.toLowerCase() !== 'default title';
      });
    const groupSuffix = pinValues.length ? ' – ' + pinValues.join(' – ') : '';
    titleEl.textContent = data.product_title + groupSuffix;
    groupEl.textContent = '';

    // Vendor / brand — static, set once. Hidden if empty.
    const vendorEl = cardEl.querySelector('[data-hp-card-vendor]');
    if (vendorEl) {
      vendorEl.textContent = data.vendor || '';
    }

    // Reviews block — show stars + count if review_count > 0, else hide
    // the whole block (per Tobias: no "0 Bewertungen" placeholder).
    // Translation: prefer data.review_text (pre-rendered server-side via
    // {{ 'products.product.reviews_count' | t: count: N }} in product-item.liquid).
    // Fall back to JS-side formatReviewCount() for PoC TEST_DATA cards.
    const reviewsEl = cardEl.querySelector('[data-hp-card-reviews]');
    if (reviewsEl) {
      const count = data.review_count || 0;
      if (count > 0) {
        const starsEl = reviewsEl.querySelector('[data-hp-card-stars]');
        const countEl = reviewsEl.querySelector('[data-hp-card-review-count]');
        if (starsEl) {
          starsEl.textContent = renderStars(data.rating);
          starsEl.setAttribute('aria-label', data.rating + ' / 5');
        }
        if (countEl) {
          countEl.innerHTML = reviewCountHTML(count, resolveReviewText(data.review_text, count));
        }
        // Link to PDP#judgeme_product_reviews — applyVariant updates the
        // ?variant= param when user changes a dropdown.
        if (reviewsEl.tagName === 'A' && data.product_url) {
          reviewsEl.href = data.product_url + '#judgeme_product_reviews';
        }
        reviewsEl.hidden = false;
      } else {
        reviewsEl.hidden = true;
      }
    }

    // HIG-39 (100% rollout): products without logistics.variants_data render a
    // LITE card — static product info + whole-card click, no variant dropdowns,
    // no delivery / cart / notify (those are hidden globally on the grid via CSS
    // until post-go-live anyway, and omitted from the lite markup entirely).
    // See docs/GO_LIVE_BLOCKERS.md + memory feedback_custom_card_global_rollout.
    if (!hasUsableData(data)) {
      const liteImg = cardEl.querySelector('[data-hp-card-image]');
      const liteLink = cardEl.querySelector('[data-hp-card-link]');
      const litePrice = cardEl.querySelector('[data-hp-card-price]');
      if (liteImg) {
        if (data.image) hpApplyImage(liteImg, data.image); // Loading-Policy + srcset zentral
        else hpPrepImg(liteImg);
        liteImg.alt = data.product_title || '';
      }
      if (liteLink && data.product_url) {
        // Pre-select this split card's variant on the PDP (HIG-64). Guard against
        // double-append in case product_url ever already carries a variant param.
        var sv = data.split_variant_id;
        liteLink.href = (sv && data.product_url.indexOf('variant=') === -1)
          ? data.product_url + (data.product_url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + sv
          : data.product_url;
        if (sv) syncBoostVariant(cardEl, sv); // Boost navigiert selbst — Daten mitziehen
      }
      if (litePrice) litePrice.textContent = formatLitePrice(data.price_min);
      applyDiscount(cardEl); // HIG-36 Teil B
      fitPriceRow(cardEl); // HIG-129: keep price + compare on one line
      applyLabels(cardEl); // HIG-36 Teil A
      attachWholeCardClick(cardEl);
      setupCarousel(cardEl); // HIG-42
      cardEl.classList.remove('hp-card--loading');
      return;
    }

    // Leave the loading state on the CTA: remove the inline spinner that
    // the skeleton injected, and drop the disabled attribute so applyVariant
    // can flip hidden/notify correctly based on the picked variant's stock.
    const atcBtn = cardEl.querySelector('[data-hp-card-atc]');
    if (atcBtn) {
      atcBtn.removeAttribute('disabled');
      const spinner = atcBtn.querySelector('.hp-card__spinner');
      if (spinner) spinner.remove();
    }

    buildSelectors(cardEl, data);

    const initial = pickInitialVariant(data);
    if (initial) {
      cardEl.querySelectorAll('[data-hp-select-option]').forEach((s) => {
        const name = s.dataset.hpSelectOption;
        s.value = initial.options[name];
      });
    }

    applyVariant(cardEl, data, initial);
    // Discount on FULL cards is PER-VARIANT (handled in applyVariant →
    // updateVariantDiscount), not the product-level applyDiscount used for lite.
    applyLabels(cardEl); // HIG-36 Teil A
    setupCarousel(cardEl); // HIG-42

    // ev.stopPropagation() on every interactive child so its click never
    // bubbles up to the whole-card click handler (which would forward to the
    // image link → unwanted redirect). My closest()-based exclusion in the
    // whole-card handler turned out to be unreliable in real browsers (worked
    // in Playwright JS-clicks, failed in real mouse clicks) — verified
    // 2026-05-31 against Chrome on iOS/Mac. Defensive stop is rock-solid.

    cardEl.querySelectorAll('[data-hp-select-option]').forEach((s) => {
      s.addEventListener('change', () => onSelectionChange(cardEl, data));
      s.addEventListener('click', (ev) => ev.stopPropagation());
      // mousedown handles the case where opening the dropdown registers as
      // mousedown but not a full click (some browsers/touch interactions)
      s.addEventListener('mousedown', (ev) => ev.stopPropagation());
    });

    const atcBtnInner = cardEl.querySelector('[data-hp-card-atc]');
    atcBtnInner.addEventListener('click', (ev) => {
      ev.stopPropagation();
      onAtcClick(cardEl, data);
    });

    const notifyBtnInner = cardEl.querySelector('[data-hp-card-notify]');
    notifyBtnInner.addEventListener('click', (ev) => {
      ev.stopPropagation();
      onNotifyClick(cardEl, data);
    });

    // Reviews link has its own native href navigation. Stop propagation so
    // the whole-card handler doesn't ALSO fire link.click() on the image link.
    const reviewsInner = cardEl.querySelector('[data-hp-card-reviews]');
    if (reviewsInner) {
      reviewsInner.addEventListener('click', (ev) => ev.stopPropagation());
    }

    // Whole-card click (shared helper; see attachWholeCardClick above).
    attachWholeCardClick(cardEl);

    // Final step: leave skeleton state. CSS rule on .hp-card--loading is
    // what shows the spinner + disabled CTA + greyed placeholders; once we
    // drop it, the cart button is interactive and the populated data shows.
    cardEl.classList.remove('hp-card--loading');
  }

  function hydrateAll() {
    hpEnsureThumbFit();
    document.querySelectorAll('[data-hp-card]').forEach(function (el) {
      try {
        hydrate(el);
      } catch (e) {
        // Never let one bad card poison the rest of the grid.
        console.warn('[hp-card] hydrate failed for', el.dataset.hpCard, e);
        el.style.display = 'none';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateAll);
  } else {
    hydrateAll();
  }

  // ---------------------------------------------------------------------------
  // Skeleton injection (Phase 1 of Two-Phase Enhancement; HIG-9 flicker fix)
  //
  // Boost SSR-paints the default product-item markup before our enhancement
  // runs. To avoid the "default Boost card briefly visible → flash → our card"
  // sequence, customization.js calls injectSkeleton(item) SYNCHRONOUSLY in
  // its MutationObserver callback — which fires as a microtask BEFORE the
  // browser's next paint cycle. So the user sees our skeleton from the very
  // first paint; the default Boost card is never visually present.
  //
  // Skeleton uses real static data from data-hp-* attrs:
  //   image, title, vendor, group placeholder, reviews (if count > 0), price.
  // The dynamic parts (variant dropdowns, delivery text, ATC interactive
  // state) are placeholder boxes — populated by hydrate(item, data) after
  // /products/X.js fetches.
  // ---------------------------------------------------------------------------

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Dynamischer (Full-Karten-)Teil des Skeletons — auch von ensureCardStructure
  // gebraucht, wenn ein Lite-Skeleton nachträglich zur Full-Karte wird (nach
  // Boost-Re-Rendern fehlen die data-hp-vd-Attribute, die Metafield-Map hat die
  // Daten aber noch → Bridge entscheidet Full auf einem Lite-Skeleton).
  function fullDynamicHTML() {
    return (
      '<div class="hp-card__selectors" data-hp-card-selectors></div>' +
      '<div class="hp-card__delivery" data-hp-card-delivery></div>' +
      '<button type="button" class="hp-card__atc" data-hp-card-atc disabled>' +
        '<span class="hp-card__spinner"></span>' +
        '<span>In den Warenkorb</span>' +
      '</button>' +
      '<button type="button" class="hp-card__notify" data-hp-card-notify hidden>' +
        'Benachrichtigen' +
      '</button>'
    );
  }

  // Speed-AP2 (2026-08-15): Strukturvertrag für die Bridge (customization.js).
  // Statt das Skeleton in Phase 2 per innerHTML zu ERSETZEN (wodurch das bereits
  // ladende Skeleton-Bild verworfen wurde), prüft die neue Bridge hiermit, ob
  // die bestehende DOM-Struktur alle Hydration-Targets hat, ergänzt fehlende
  // Full-Teile und lässt Media + Bild UNANGETASTET. Rückgabe false = Struktur
  // nicht wiederverwendbar → die Bridge fällt auf ihren alten innerHTML-Pfad
  // zurück (und altes Theme-JS ohne diese Funktion ebenso). Beide Richtungen
  // des Push-Fensters bleiben damit funktionsfähig.
  function ensureCardStructure(itemEl, isFull) {
    const media = itemEl.querySelector('.hp-card__media');
    const img = media && media.querySelector('[data-hp-card-image]');
    const body = itemEl.querySelector('.hp-card__body');
    if (!media || !img || !body) return false;
    const need = ['[data-hp-card-title]', '[data-hp-card-vendor]', '[data-hp-card-group]', '[data-hp-card-price]'];
    for (let i = 0; i < need.length; i++) {
      if (!body.querySelector(need[i])) return false;
    }
    const rev = body.querySelector('[data-hp-card-reviews]');
    if (!rev) return false;
    // Skeleton mit review_count=0 rendert den Reviews-Link LEER — die inneren
    // Spans nachrüsten, damit hydrate() sie bei später vorhandenen Daten füllt.
    if (!rev.querySelector('[data-hp-card-stars]')) {
      rev.innerHTML =
        '<span class="hp-card__stars" data-hp-card-stars></span>' +
        '<span class="hp-card__review-count" data-hp-card-review-count></span>';
    }
    if (isFull && !body.querySelector('[data-hp-card-selectors]')) {
      body.insertAdjacentHTML('beforeend', fullDynamicHTML());
    }
    return true;
  }

  function buildSkeletonHTML(itemEl) {
    const ds = itemEl.dataset;
    const title = ds.hpTitle || '';
    const image = ds.hpImage || '';
    const url = ds.hpProductUrl || '#';
    const vendor = ds.hpVendor || '';
    const priceMin = ds.hpPriceMin || '';
    const rating = parseRating(ds.hpRating);
    const reviewCount = parseInt(ds.hpReviewCount || '0', 10) || 0;
    const reviewText = reviewCount > 0 ? resolveReviewText(ds.hpReviewText, reviewCount) : '';

    // Reviews block: include only if there are any reviews. Pre-render
    // stars + (translated) count from SSR attrs so it's visible from
    // frame 0; hydrate() leaves it alone for review_count > 0.
    // Element is an <a> linking to PDP#judgeme_product_reviews.
    let reviewsHtml = '';
    if (reviewCount > 0) {
      reviewsHtml =
        '<a class="hp-card__reviews" data-hp-card-reviews href="' + escapeHtml(url) + '#judgeme_product_reviews">' +
          '<span class="hp-card__stars" data-hp-card-stars aria-label="' +
            escapeHtml(rating) + ' / 5">' +
            renderStars(rating) +
          '</span>' +
          '<span class="hp-card__review-count" data-hp-card-review-count>' +
            reviewCountHTML(reviewCount, reviewText) +
          '</span>' +
        '</a>';
    } else {
      reviewsHtml = '<a class="hp-card__reviews" data-hp-card-reviews hidden></a>';
    }

    // HIG-39: a card is "lite" when no logistics.variants_data was emitted
    // (data-hp-vd absent/empty). Lite cards have no variant dropdowns, so we
    // omit the selectors placeholder (would otherwise show a grey box that never
    // fills) and the delivery/cart/notify block (hidden globally on the grid
    // anyway). Full (opted-in) cards keep the original skeleton with placeholders.
    const isLite = !(ds.hpVd && ds.hpVd !== 'null' && ds.hpVd !== '""');
    const dynamicHtml = isLite ? '' : fullDynamicHTML();

    // Speed-AP2 Loading-Policy: erste sichtbare Reihe eager (Karte 1 zusätzlich
    // fetchpriority=high = wahrscheinlichster LCP-Kandidat), Rest lazy. srcset/
    // sizes von Geburt an, damit die Hydration nie den Kandidaten wechselt
    // (siehe hpApplyImage). width/height sind layout-inert (CSS setzt 100%/100%
    // im quadratischen .hp-card__media-Slot), beschreiben aber das Seitenformat.
    const gridIdx = hpGridIndex(itemEl);
    const skelSrcset = hpSrcset(image);
    const imgAttrs =
      (skelSrcset ? 'srcset="' + escapeHtml(skelSrcset) + '" sizes="' + escapeHtml(CARD_SIZES) + '" ' : '') +
      'width="600" height="600" ' +
      (gridIdx < hpGridCols()
        ? 'fetchpriority="high" ' /* Speed-AP4 WP6: ganze erste Reihe hoch priorisieren (LCP-Kandidat ist oft Karte 2/3) */
        : 'loading="lazy" ');

    return (
      '<a class="hp-card__media" data-hp-card-link href="' + escapeHtml(url) + '">' +
        '<img class="hp-card__image" data-hp-card-image crossorigin="anonymous" ' + imgAttrs + 'src="' +
          escapeHtml(hpSized(image)) +
          '" alt="' + escapeHtml(title) + '" />' +
      '</a>' +
      '<div class="hp-card__body">' +
        '<h3 class="hp-card__title" data-hp-card-title>' + escapeHtml(title) + '</h3>' +
        '<div class="hp-card__meta">' +
          '<span class="hp-card__vendor" data-hp-card-vendor>' + escapeHtml(vendor) + '</span>' +
          '<span class="hp-card__group" data-hp-card-group></span>' +
        '</div>' +
        reviewsHtml +
        '<div class="hp-card__price" data-hp-card-price>' + escapeHtml(priceMin) + '</div>' +
        dynamicHtml +
      '</div>'
    );
  }

  function injectSkeleton(itemEl) {
    // Idempotent: skip if already enhanced or skeleton-rendered.
    if (itemEl.classList.contains('hp-card') ||
        itemEl.classList.contains('hp-card--loading')) {
      return;
    }
    itemEl.innerHTML = buildSkeletonHTML(itemEl);
    itemEl.classList.add('hp-card--loading');
    // Varianten-Vorauswahl schon ab dem ERSTEN Frame absichern (2026-08-02).
    // syncBoostVariant lief bisher nur in der Hydration, also NACH dem
    // /products/<handle>.js-Abruf. Wer vorher klickt — oder wessen Abruf hängt —
    // bekam Boosts nackte Produkt-URL und damit die Default-Variante. Die
    // Gruppen-Variante steht dagegen schon serverseitig in data-product, kostet
    // hier nichts und macht aus einem Totalausfall schlimmstenfalls «richtige
    // Farbe, erste Grösse». applyVariant verfeinert sie danach auf die im
    // Dropdown gewählte. Siehe docs/COLLECTION_CARD.md.
    try {
      var dp = JSON.parse((itemEl.getAttribute('data-product') || '{}').replace(/&quot;/g, '"'));
      if (dp && dp.variantId) syncBoostVariant(itemEl, dp.variantId);
    } catch (e) { /* no-op */ }
  }

  // Expose hydrate + skeleton hooks so external bridges (Boost's
  // customization.js running after their client-side render) can:
  //   1. injectSkeleton(item) synchronously in their MutationObserver →
  //      gives Phase 1 (visible from first paint).
  //   2. hydrate(item, data) asynchronously after /products/<handle>.js →
  //      gives Phase 2 (interactive, full variant data).
  // See docs/COLLECTION_CARD.md (Two-Phase Enhancement architecture).
  window.HPCollectionCard = {
    pickInitialVariant: pickInitialVariant,
    hydrate: hydrate,
    hydrateAll: hydrateAll,
    injectSkeleton: injectSkeleton,
    buildSkeletonHTML: buildSkeletonHTML,
    // Speed-AP2: Bridge-Vertrag — Skeleton-DOM (inkl. ladendem Bild) behalten
    // statt in Phase 2 per innerHTML zu ersetzen. Siehe ensureCardStructure.
    ensureCardStructure: ensureCardStructure,
    // HIG-124: the Choose-Options (quick-add) drawer reuses the card's compact
    // delivery rendering so it reads identically to the category cards. Pass a
    // variant's stock_breakdown array (from logistics.variants_data); returns the
    // same per-bucket line HTML (incl. the OOS bell line for an empty breakdown).
    renderDelivery: renderBreakdownHTML,
  };
})();
/* sync trigger */
